import * as React from "react";

import "./password.scss";

import {cx}                                 from "emotion";
import ReactTooltip                         from "react-tooltip";

import {EMAIL_REGEX}                        from "@Common/react/cs-form/validators/email-format";
import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                                           from "@Common/react/cst2";
import {scopedState}                        from "@Common/react/scoped-state";
import {UseState}                           from "@Common/react/use-state";
import {Types}                              from "@Common/types";
import {AlertModal}                         from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {ReCaptcha}                          from "@Common/ui-components/recaptcha/recaptcha";
import {chain}                              from "@Common/utils/fs";

import {consumeContext}                     from "@Xpresso/loaders/consume-context";


import {SubHeader}                          from "../common/sub-header/sub-header";
import {Layout}                             from "../layout/layout";

declare global {
    interface Window {
        onCaptchaLoad   ?: () => void,
    }
}

type ApiRequest = Types.Apis.Payloads.Next.Authenticate.GetUserNames.Request;
type ApiResponse = Types.Apis.Payloads.Next.Authenticate.GetUserNames.Response;

export const PasswordRoute = () => (
    makeChain<JSX.Element>()
    .then(...consumeContext("themeScheme"))
    .then(({
        themeScheme,
    }, next:Link<void>) => (
    <Layout>
        <SubHeader
            backLink={{}}
            title="Reset Username/Password"
        />
        <Support/>
    </Layout>
    ))
    .run()
);

const Support = () => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then("formState", ({}, next:ComponentLink<typeof UseState<Partial<ApiRequest>>>) => (
        UseState({next, initValue: {}})
    ))
    .then("formError", value(({formState: {value: formValue}}) => (!formValue.captcha
        ? "Please complete the ReCAPTCHA challenge"
        : FORM_FIELDS.reduce((previousError, {key, label, validators}) => {
            if (previousError) {
                return previousError;
            }

            const fieldValue        = formValue[key];
            const failingValidator  = validators?.find(({filter}) => !filter(fieldValue));
            const error             = failingValidator?.makeError({value: fieldValue});
            return error ? `${label}: ${error}` : null;
        }, null as string)
    )))

    .then("submittingState", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({next}))
    .then("usernameState", ({}, next:ComponentLink<typeof UseState<ApiResponse["data"]>>) => UseState({next}))
    .then("successDialog", ({}, next:ComponentLink<typeof AlertModal>) => AlertModal({next}))
    .then("captchaTokenState", value(({formState}) => scopedState(formState, ["captcha"])))
    .then(({
        apis,
        captchaTokenState: {onChange: setCaptchaToken},
        formError,
        formState,
        submittingState: {value: submitting, onChange: setSubmitting},
        successDialog,
        usernameState,
    }, next:Link<void>) => !usernameState.value ? (
        <main className="password-i8f">
            <header><span className="title" xd-qa-type="label" xd-qa-name="Title">Please type in your e-mail below to help us locate your account.</span></header>
            <section>
                {FORM_FIELDS.map(({inputMaker: Input, label, key, validators}) => (
                    chain(
                        scopedState(formState, [key]),
                        ({onChange: setFieldValue, value: fieldValue}) => (
                            <div key={key} className={cx("input-field", {required: validators?.includes(VALIDATORS.required)})}>
                                <label xd-qa-type="label" xd-qa-name={label}>{label}</label>
                                <Input
                                    disabled={submitting}
                                    label={label}
                                    onChange={setFieldValue}
                                    onKeyDown={(e) => e.key === "Enter" && !(submitting || !!formError) && next()}
                                    value={fieldValue ?? ""}
                                    xd-qa-type="textbox"
                                    xd-qa-name={label + " Input Field"}
                                />
                            </div>
                        )
                    )
                ))}
                <div className="recaptcha" xd-qa-type="card" xd-qa-name="Recaptcha">
                    <ReCaptcha onExpiration={() => setCaptchaToken(null)} onVerification={setCaptchaToken}/>
                </div>
                <button
                    disabled={submitting || !!formError}
                    onClick={next}
                    xd-qa-type="button"
                    xd-qa-name="Find My Account"
                >
                    Find My Account
                </button>
                {formError && <em className="error" xd-qa-type="text" xd-qa-name="Error Message">{formError}</em>}
            </section>
        </main>
    ) : (
        <main className="password-i8f">
            <ReactTooltip  id="password" place="bottom" delayShow={100}/>
            <div
                className="back"
                onClick={() => usernameState.onChange()}
                xd-qa-type="link"
                xd-qa-name="Back"
            >
                <i className="fa fa-chevron-left"/>
                Back
            </div>
            <header><span className="title" xd-qa-type="label" xd-qa-name="Title">
                We have located the following account(s) associated with the provided information.
                Click "Send Email" to receive a login link to your email.
            </span></header>
            <section>
                {usernameState.value.map((user) => (
                    <div className="card" key={user.idUser} xd-qa-type="card" xd-qa-name={user.idUser} >
                        <div className="data">
                            <div className="label" xd-qa-type="label" xd-qa-name="Company">Company:</div>
                            <div
                                className="value" xd-da-type="text" xd-qa-name="Company Name"
                                data-for="password" data-tip={user.companyName}
                            >{user.companyName}</div>
                        </div>
                        <div className="data">
                            <div className="label" xd-qa-type="label" xd-qa-name="Office">
                                Office:
                                {!user.branchActive && <span xd-qa-type="badge" xd-qa-name="Inactive" className="badge inactive-badge">Inactive</span>}
                            </div>
                            <div
                                className="value" xd-da-type="text" xd-qa-name="Office Name"
                                data-for="password" data-tip={user.branchName}
                            >{user.branchName}</div>
                        </div>
                        <div className="data">
                            <div className="label" xd-qa-type="label" xd-qa-name="User">
                                User:
                                {(!user.agentActive || !user.userActive ) &&
                                    <span xd-qa-type="badge" xd-qa-name="Inactive" className="badge inactive-badge">Inactive</span>
                                }
                            </div>
                            <div
                                className="value" xd-da-type="text" xd-qa-name="User Name"
                                data-for="password" data-tip={user.agentName}
                            >{user.agentName}</div>
                        </div>
                        <div className="data">
                            <div className="label" xd-qa-type="label" xd-qa-name="User Level">User Level:</div>
                            <div className="value" xd-da-type="text" xd-qa-name="User Level">
                                {getUserBadge(user)}
                            </div>
                        </div>
                        <button
                            disabled={!ableToActivate(user) || submitting}
                            onClick={() => sendEmail({apis, formValue: formState.value, setSubmitting, successDialog, idUser: user.idUser})}
                            xd-da-type="button"
                            xd-qa-name="Send Email"
                            { ...(ableToActivate(user) ? {} : { "data-for": "password", "data-tip": "This company does not allow account reactivation" }) }
                        >
                            {`${ableToActivate(user) && isUserInactive(user) ? 'Activate and ' : ''}Send Email`}
                        </button>
                    </div>
                ))}
            </section>
        </main>
    ))

    // onSubmit
    .then(async ({
        apis, usernameState, formState: {value: formValue},
        submittingState: {onChange: setSubmitting}, successDialog,
    }, next:Link<void>) => {
        setSubmitting(true);
        const response = await apis.next.authenticate.getUserNames({...formValue} as ApiRequest);
        setSubmitting(false);
        let usernames = [];
        if(response.statusCode !== "success") {
            successDialog.show({
                message: "Something went wrong",
                description: `Error Code: ${response?.statusCode}: Please go back and try this action again.`
            });
            return;
        } else {
            usernames = response.data;
        }
        if(usernames.length === 0) {
            successDialog.show({
                message: "No account found",
                description: "Unfortunately we weren't able to find an account with the provided information.",
                description2: "Please try a different email address."
            });
            grecaptcha.reset()
        } else if(usernames.length === 1) {
            sendEmail({apis, formValue, setSubmitting, successDialog, idUser: usernames[0].idUser})
            grecaptcha.reset()
        } else {
            grecaptcha.reset()
            usernameState.onChange(usernames)
        }
    })
    .run()
);

interface FieldValidator {
    filter      : (value:string) => boolean,
    makeError   : (context:{value:string}) => string,
}

interface InputProps {
    disabled    ?: boolean,
    label        : string,
    onChange     : (value:string) => void,
    onKeyDown   ?: (e:React.KeyboardEvent<HTMLInputElement>) => void,
    value        : string,
}


const SimpleInput = ({disabled, label, onChange: emitChange, onKeyDown, type, value}:InputProps & {type?: string}) => (
    <input
        {...{disabled, type, value}}
        onChange={(e) => emitChange(e.target.value)}
        onKeyDown={onKeyDown}
        xd-qa-type="field"
        xd-qa-name={label}
    />
);


const VALIDATORS:Record<string, FieldValidator> = {
    email       : {
        filter      : (value) => EMAIL_REGEX.test(value),
        makeError   : () => "Please enter a valid email address",
    },
    required    : {
        filter      : (value) => !!value,
        makeError   : () => "This field is required",
    },
};

const FORM_FIELDS:Array<{
    inputMaker  ?: (props:InputProps) => JSX.Element,
    key          : keyof ApiRequest,
    label        : string,
    validators  ?: Array<FieldValidator>,
}> = [
    {key: "emailAddress",  inputMaker: (props) => SimpleInput({...props, type: "email"}),  label: "Email Address",
        validators: [VALIDATORS.required, VALIDATORS.email]},
];

const sendEmail = async ({setSubmitting, apis, formValue, successDialog, idUser}) => {
    setSubmitting(true);
    const response = await apis.next.authenticate.sendLoginEmail({
        emailAddress: formValue.emailAddress,
        idUser,
    });
    if(response.statusCode !== "success") {
        successDialog.show({
            message: "Something went wrong",
            description: `Error Code: ${response?.statusCode}: Please try again.`
        });
    } else {
        successDialog.show({
            message: "Email Sent",
            description: "An email containing a link to login has been sent to your inbox. ",
            description2: "Once logged in, you will be able to update your username and/or password for this account."
        })
    }
    setSubmitting(false);
}

const isUserInactive = (user) => !user.agentActive || !user.userActive || !user.branchActive;

const ableToActivate = (user) =>
    ( user.autoActivateAgent || user.agentActive ) &&
    ( user.autoActivateUser || user.userActive ) &&
    ( user.autoActivateBranch || user.branchActive );

const getUserBadge = (user) => {
    switch(user.userLevel) {
        case "Office":
            return <span xd-qa-type="badge" xd-qa-name="Office Admin" className="badge office-admin-badge">Office Admin</span>
        case "Company":
            return <span xd-qa-type="badge" xd-qa-name="Company Admin" className="badge company-admin-badge">Company Admin</span>
        default:
            return "User";
    }
}
